'use client'

// R2 wave 2: save/unsave a workflow (user_preferences kind=saved_workflow). Logged-out
// click routes to /login with a return path. Styled by p2.css (.sw-save) — mounted in the
// P2 hub hero. PostHog (fix 6, 2026-10-16): saving or unsaving sends one `favButtonClicked`
// (kind 'saved_workflow'), the same event as the heart, once the write has landed.
import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useSupabaseAuth } from '@/context/supabaseContext'
import { SUPABASE_CONFIGURED } from '@/lib/supabase/client'
import { isWorkflowSaved, toggleWorkflowSaved } from '@/lib/supabase/engagement'
import { trackEvent } from '@/lib/analytics/posthog'
import { favouriteEventAfterWrite } from '@/lib/analytics/favourites'
import { recordMemberActivity } from '@/lib/analytics/memberActivity'

export function SaveWorkflowButton({ path, title }: { path: string; title: string }) {
  const router = useRouter()
  const { user, isLoaded } = useSupabaseAuth()
  const [saved, setSaved] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!isLoaded) return
    if (!user) {
      setSaved(false)
      return
    }
    isWorkflowSaved(path).then(setSaved)
  }, [isLoaded, user, path])

  if (!SUPABASE_CONFIGURED) return null

  const onClick = async () => {
    if (!user) {
      router.push(`/login?next=${encodeURIComponent(path)}`)
      return
    }
    if (busy || saved === null) return
    setBusy(true)
    const wanted = !saved
    const now = await toggleWorkflowSaved(path, title)
    setSaved(now)
    setBusy(false)
    const event = favouriteEventAfterWrite({ path, wanted, stored: now, kind: 'saved_workflow' })
    if (event) {
      trackEvent(event.name, event.properties)
      // only a save/unsave that reached the database (EA's own record, member_activity)
      void recordMemberActivity(event.properties.action === 'add' ? 'save' : 'unsave', {
        target: event.properties.content_path,
      })
    }
  }

  return (
    <button
      type="button"
      className={`sw-save${saved ? ' is-saved' : ''}`}
      onClick={onClick}
      disabled={busy || (isLoaded && Boolean(user) && saved === null)}
      aria-pressed={Boolean(saved)}
    >
      {saved ? '★ Saved to your workflows' : '☆ Save workflow'}
    </button>
  )
}
